import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '../context/AuthContext';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, MapPin, MessageCircle, User, Calendar, ChevronDown } from 'lucide-react';

// WantDetail component loaded (production)

export default function WantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [want, setWant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showContactForm, setShowContactForm] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    fetchWant();
  }, [id]);

  const fetchWant = async () => {
    try {
      const { data } = await api.get(`/wants/${id}`);
      setWant(data.want);
    } catch (err) {
      console.error('Error fetching want:', err);
      navigate('/wants');
    } finally {
      setLoading(false);
    }
  };

  const handleContact = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    setShowContactForm(true);
  };

  const sendMessage = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post('/wants/contact', {
        want_id: id,
        message: message
      });
      // Message sent response (production):
      if (data?.success) {
        alert('Mensaje enviado exitosamente!');
        // Navigate to the new wants chat with the conversation
        if (data.conversation_id) {
          navigate(`/wants-chat/${data.conversation_id}`);
        } else {
          navigate('/wants-chat');
        }
      }
    } catch (err) {
      console.error('Error sending message:', err);
      // Check if it's an auth error
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        alert(err.response?.data?.error || 'Error al enviar mensaje');
      }
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!want) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-16 md:top-32 z-20">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <button 
            onClick={() => navigate('/wants')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          {/* Content */}
          <div className="p-6">
            {/* Category */}
            {want.category && (
              <span className="inline-block bg-gray-100 text-sm px-3 py-1 rounded-full mb-4">
                {want.category.brand} {want.category.model}
              </span>
            )}

            {/* Images */}
            {want.images && want.images.length > 0 && (
              <div className="mb-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {want.images.map((img, idx) => (
                    <img 
                      key={img.id || idx}
                      src={img.url}
                      alt={`Imagen ${idx + 1}`}
                      className="w-full aspect-square object-cover rounded-lg"
                      onError={(e) => { e.target.onerror=null; e.target.src='data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27100%27 height=%27100%27%3E%3Crect width=%27100%27 height=%27100%27 fill=%27%23f3f4f6%27/%3E%3Ctext x=%2750%25%27 y=%2750%25%27 dominant-baseline=%27middle%27 text-anchor=%27middle%27 font-size=%2730%27 fill=%27%23d1d5db%27%3E🖼%3C/text%3E%3C/svg%3E'; }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Title */}
            <h1 className="text-2xl md:text-3xl font-bold mb-4">{want.title}</h1>

            {/* Budget */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-green-700 mb-1">Presupuesto disponible</p>
              <p className="text-2xl font-bold text-green-700">
                ₡{want.budget_min?.toLocaleString('es-CR')}
                {want.budget_max && (
                  <span className="text-lg font-normal"> - ₡{want.budget_max.toLocaleString('es-CR')}</span>
                )}
              </p>
            </div>

            {/* Description */}
            {want.description && (
              <div className="mb-6">
                <h3 className="font-semibold mb-2">Descripción</h3>
                <p className={`text-gray-600 ${!expanded && 'line-clamp-3'}`}>
                  {want.description}
                </p>
                {want.description.length > 200 && (
                  <button 
                    onClick={() => setExpanded(!expanded)}
                    className="text-primary text-sm mt-1 flex items-center gap-1"
                  >
                    {expanded ? 'Ver menos' : 'Ver más'}
                    <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                  </button>
                )}
              </div>
            )}

            {/* Location & Date */}
            <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-6">
              {want.province && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {want.province.replace('_', ' ')}
                </div>
              )}
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {formatDate(want.created_at)}
              </div>
            </div>

            {/* User Info */}
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl mb-6">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-lg">
                {want.user?.name?.charAt(0) || '?'}
              </div>
              <div className="flex-1">
                <p className="font-semibold">{want.user?.name}</p>
                <p className="text-sm text-gray-500">
                  {want.user?.total_products || 0} productos publicados
                </p>
              </div>
            </div>

            {/* Contact Button */}
            {!showContactForm ? (
              <button
                onClick={handleContact}
                className="w-full bg-yellow-400 border-2 border-black text-black hover:bg-yellow-500 py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition"
              >
                <MessageCircle className="w-5 h-5" />
                Contactar al comprador
              </button>
            ) : (
              <div className="bg-gray-50 rounded-xl p-4">
                <label className="block font-medium mb-2">Tu mensaje:</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={`Hola, me interesa la pieza que buscas: "${want.title}". Tengo disponible...`}
                  className="w-full border rounded-lg p-3 min-h-[120px] resize-none"
                />
                <div className="flex gap-3 mt-3">
                  <button
                    onClick={sendMessage}
                    disabled={sending || !message.trim()}
                    className="flex-1 bg-primary text-white py-3 rounded-lg font-medium disabled:opacity-50"
                  >
                    {sending ? 'Enviando...' : 'Enviar mensaje'}
                  </button>
                  <button
                    onClick={() => setShowContactForm(false)}
                    className="px-6 border py-3 rounded-lg"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const WANT_PLACEHOLDER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='400'%3E%3Crect width='800' height='400' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-size='64' fill='%23d1d5db'%3E🔧%3C/text%3E%3C/svg%3E";

function getWantImage(title) {
  return WANT_PLACEHOLDER;
}

function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-CR', { month: 'long', day: 'numeric', year: 'numeric' });
}